import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Topics } from '../../../models/LocalNotificationTopics';
import { AppAutoUpdaterEvent, AppAutoUpdaterEventName, IAppAutoUpdaterEvent, IBuildInfo } from '../../../models/index';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../LocalNotificationObserver/WithNotificationService';

/**
 * Routes on which a silent auto-refresh is safe.
 * Only read-only pages with no unsaved user state should be listed here.
 * Refresh is BLOCKED on all other routes by default (allow-list model).
 */
export const ALLOWED_ROUTE_PREFIXES: readonly string[] = ['/dashboard'];

/**
 * Returns true when the current page is on the auto-refresh allow-list.
 * Accepts an optional `pathname` argument for testing; defaults to `window.location.pathname`.
 */
export function isOnAutoRefreshAllowedRoute(pathname: string = window.location.pathname): boolean {
	return ALLOWED_ROUTE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(prefix + '/'));
}

interface IProps extends IEventLoggingComponentProps, INotificationServiceComponentProps<IAppAutoUpdaterEvent> {
	onWillBroadcastRefreshNotification?(e: IAppAutoUpdaterEvent): void;
}

class _AppAutoUpdater extends React.Component<IProps> {
	private mDomMutationObserver: MutationObserver;
	private mLastDomMutationMoment: moment.Moment;
	private mMounted: boolean;
	private mPendingRefreshEvent: IAppAutoUpdaterEvent;
	private mUpdateTimeoutHandle: any;
	private static TimeOutDuration = 600000; // 10 min

	constructor(props: IProps) {
		super(props);

		makeObservable<_AppAutoUpdater, 'mLastDomMutationMoment' | 'appIsIdle' | 'onDomMutation' | 'clearTimeout'>(this, {
			mLastDomMutationMoment: observable.ref,
			appIsIdle: computed,
			onDomMutation: action,
			clearTimeout: action,
		});
	}

	public componentDidMount() {
		this.mMounted = true;
		this.mLastDomMutationMoment = moment();
		this.toggleUserInputObserver(true);
		this.startPollingForBuildInfoChanges();
	}

	public componentWillUnmount() {
		this.mMounted = false;
		this.clearTimeout();
		this.toggleUserInputObserver(false);
		this.onPendingRefreshCanceled();
	}

	public render() {
		return null as JSX.Element;
	}

	private get appIsIdle() {
		return moment().diff(this.mLastDomMutationMoment, 'minutes') > 5;
	}

	private toggleUserInputObserver = (on: boolean) => {
		if (window?.MutationObserver) {
			this.mDomMutationObserver?.disconnect();

			this.mDomMutationObserver = null;
			if (on) {
				const reactRootElement = document.getElementById('react-root');
				if (reactRootElement) {
					this.mDomMutationObserver = new MutationObserver(this.onDomMutation);
					this.mDomMutationObserver.observe(reactRootElement, {
						attributes: true,
						childList: true,
						subtree: true,
					});
				}
			}
		}
	};

	private onDomMutation = () => {
		this.mLastDomMutationMoment = moment();
	};

	private startPollingForBuildInfoChanges = () => {
		this.clearTimeout();
		this.mUpdateTimeoutHandle = setTimeout(() => {
			this.getBuildInfo();
		}, _AppAutoUpdater.TimeOutDuration);
	};

	private getBuildInfo = () => {
		const { logEvent, onWillBroadcastRefreshNotification, postNotification } = this.props;
		if (this.mMounted) {
			this.clearTimeout();
			fetch('/static/build-info.json')
				?.then(async res => {
					try {
						const buildInfo: IBuildInfo = await res.json();
						if (!!buildInfo && !!this.mMounted) {
							const shouldBroadcastUpdateNotification =
								__BUILD_HASH__ !== buildInfo.hash && !!this.appIsIdle && isOnAutoRefreshAllowedRoute();
							if (shouldBroadcastUpdateNotification) {
								const event = new AppAutoUpdaterEvent(AppAutoUpdaterEventName.AppAutoUpdaterWillRefresh, {
									onDefaultPrevented: this.onPendingRefreshCanceled,
									target: this,
								});
								if (onWillBroadcastRefreshNotification) {
									onWillBroadcastRefreshNotification(event);
								}

								if (!event.defaultPrevented) {
									this.mPendingRefreshEvent = event;

									postNotification({
										info: event,
										topic: Topics.APP_REFRESH,
									});
									setTimeout(() => {
										if (this.mMounted) {
											if (!event.defaultPrevented && !!this.appIsIdle) {
												logEvent('Refresh');
												if (window?.location?.reload) {
													window.location.reload();
												}
												return;
											}

											if (event === this.mPendingRefreshEvent) {
												this.mPendingRefreshEvent = null;
											}

											if (!this.mUpdateTimeoutHandle && !this.mPendingRefreshEvent) {
												this.startPollingForBuildInfoChanges();
											}
										}
									}, 5000);
									return;
								}
							}
							this.startPollingForBuildInfoChanges();
						}
					} catch {
						this.startPollingForBuildInfoChanges();
					}
				})
				.catch(() => {
					this.startPollingForBuildInfoChanges();
				});
		}
	};

	private clearTimeout() {
		if (this.mUpdateTimeoutHandle) {
			clearTimeout(this.mUpdateTimeoutHandle);
			this.mUpdateTimeoutHandle = null;
		}
	}

	private onPendingRefreshCanceled = () => {
		this.mPendingRefreshEvent = null;
	};
}

const AppAutoUpdaterAsObserver = observer(_AppAutoUpdater);
const AppAutoUpdatedWithNotificationService = withNotificationService(AppAutoUpdaterAsObserver);
export const AppAutoUpdater = withEventLogging(AppAutoUpdatedWithNotificationService, 'AppAutoUpdater');
